/**
 * CHECKPOINT — semanas 4, 8 y 12.
 *
 * Compara desempeño con el baseline usando TAREAS NUEVAS comparables, nunca
 * repitiendo las respuestas del baseline. Queremos evidencia de cambio, no
 * familiaridad con una respuesta ensayada.
 *
 * REGLA DURA: la aplicación NO muestra las respuestas ni las notas anteriores
 * ANTES de hacer el checkpoint. Solo después, en la comparación. Enseñarlas
 * antes convertiría el checkpoint en un guion memorizado.
 *
 * ChatGPT no es un instrumento fonético de laboratorio y el prompt se lo dice:
 * debe separar observaciones confiables de juicios de audio inciertos.
 *
 * DECISIÓN A (aprobada): el paso de listening usa una MUESTRA AUTÉNTICA —un
 * clip nuevo y desconocido— y no una búsqueda de frase en YouGlish. En W4, W8 y
 * W12 la medición tiene que ser tan limpia como la del baseline.
 */

import type { Sesion } from '../../tipos.js';
import { FALLBACK_SIN_FUENTE } from '../fuentesListening.js';

export const CHECKPOINT: Sesion = {
  clave: 'checkpoint',
  tipo: 'checkpoint',
  etiqueta: 'Checkpoint',
  titulo: 'Checkpoint · medir progreso',
  corto: 'Checkpoint',
  objetivo: 'Comparar tu desempeño con el baseline usando tareas nuevas.',
  porQue:
    'Queremos evidencia de cambio, no familiaridad con una respuesta ensayada. Por eso las tareas son nuevas y no ves tus respuestas anteriores hasta terminar.',
  areasAEvaluar: [
    'habla_espontanea',
    'listening',
    'recuperacion_lexica',
    'naturalidad',
    'interaccion_turnos',
  ],
  capturaCorrecciones: true,
  planes: {
    20: {
      duracion: 20,
      pasos: [
        {
          clave: 'cp-muestra',
          titulo: 'Muestra nueva',
          minutosSugeridos: 2,
          herramienta: 'grabadora',
          grabadora: true,
          objetivo: 'Guardar habla espontánea comparable con la del baseline.',
          porQue:
            'La muestra tiene que ser nueva, no el mismo monólogo. Si repetís el tema del baseline, medís memoria y no progreso.',
          instrucciones: [
            'Hablá 2 minutos sobre un tema que NO hayas preparado.',
            'No uses el mismo tema del baseline.',
            'Guardala con la etiqueta del checkpoint que estás haciendo.',
          ],
          uso: {
            queHacer:
              'Grabá 2 minutos sobre un tema nuevo y guardala con la etiqueta del checkpoint (W4, W8 o W12).',
          },
          areas: ['habla_espontanea', 'pronunciacion_prosodia'],
        },
        {
          clave: 'cp-voice',
          titulo: 'Checkpoint por Voice',
          minutosSugeridos: 13,
          herramienta: 'chatgpt',
          prompt: 'checkpoint',
          objetivo: 'Medir tu desempeño general con tareas comparables.',
          porQue:
            'Usamos funciones comparables a las del baseline, no preguntas idénticas. Eso reduce el efecto memoria.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'No practiques antes: eso arruinaría la medición.',
            'No pidas coaching durante la performance.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y hacé las tareas sin coaching. El resumen viene al final.',
          },
          areas: [
            'habla_espontanea',
            'recuperacion_lexica',
            'naturalidad',
            'interaccion_turnos',
            'gramatica_fluidez',
          ],
        },
        {
          clave: 'cp-resumen',
          titulo: 'Resumen del checkpoint',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 5 minutos.',
          objetivo: 'Registrar los cambios que notás.',
          porQue:
            'Buscamos cambios consistentes, no un buen día. Comparar contra tus observaciones previas, no contra tus expectativas.',
          instrucciones: [
            'Puntuá fluidez, pausas, recuperación, listening, naturalidad e interacción.',
            'Guardá también qué mejoró y qué sigue igual.',
            'No cambies el plan por un solo mal día.',
          ],
        },
      ],
    },

    35: {
      duracion: 35,
      pasos: [
        {
          clave: 'cp-muestra',
          titulo: 'Muestra nueva',
          minutosSugeridos: 2,
          herramienta: 'grabadora',
          grabadora: true,
          objetivo: 'Guardar habla comparable.',
          porQue: 'Sirve para el contraste longitudinal contra la muestra de Semana 0.',
          instrucciones: [
            'Hablá 2 minutos sin preparar, sobre un tema nuevo.',
            'Guardala con la etiqueta del checkpoint.',
          ],
          uso: {
            queHacer:
              'Grabá 2 minutos sobre un tema nuevo y guardala con la etiqueta del checkpoint (W4, W8 o W12).',
          },
          areas: ['habla_espontanea', 'pronunciacion_prosodia'],
        },
        {
          clave: 'cp-voice',
          titulo: 'Checkpoint por Voice',
          minutosSugeridos: 20,
          herramienta: 'chatgpt',
          prompt: 'checkpoint',
          objetivo: 'Evaluar interacción y producción.',
          porQue: 'Tareas nuevas reducen el efecto memoria y miden capacidad, no repetición.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Hacelo sin coaching.',
            'No te prepares antes.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y completá las tareas sin pedir ayuda durante la performance.',
          },
          areas: [
            'habla_espontanea',
            'recuperacion_lexica',
            'naturalidad',
            'interaccion_turnos',
            'gramatica_fluidez',
          ],
        },
        {
          clave: 'cp-listening',
          titulo: 'Muestra auténtica de listening',
          minutosSugeridos: 8,
          herramienta: 'muestraListening',
          objetivo: 'Comprobar tu comprensión de habla auténtica desconocida.',
          porQue:
            'El listening puede mejorar de forma distinta al speaking. Medirlo aparte, y con material que no conocés, evita confundir los dos progresos.',
          instrucciones: [
            'Abrí un clip NUEVO de entre 30 y 90 segundos que no hayas escuchado antes.',
            'Usá una fuente distinta de la del checkpoint anterior si podés.',
            'Primera escucha SIN subtítulos ni transcripción.',
            'Resumí en inglés lo que entendiste.',
            'Comprobá después con el texto y anotá la dificultad percibida.',
          ],
          uso: {
            queHacer:
              'Elegí una fuente sugerida, tomá un fragmento nuevo de 30–90 segundos, escuchalo una vez sin texto, resumí y comprobá al final.',
            fuentesSugeridas: ['npr', 'thisAmericanLife', 'pbsNewsHour', 'youtubeCanalPropio'],
            fallbackSinFuente: FALLBACK_SIN_FUENTE,
          },
          areas: ['listening'],
        },
        {
          clave: 'cp-resumen',
          titulo: 'Resumen del checkpoint',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 5 minutos.',
          objetivo: 'Registrar cambios y fijar prioridades.',
          porQue: 'Compará con tus observaciones previas, no con lo que esperabas conseguir.',
          instrucciones: [
            'Guardá 3 mejoras que notaste.',
            'Guardá 1 o 2 prioridades para las próximas semanas.',
            'Puntuá las áreas de la sesión.',
          ],
        },
      ],
    },

    50: {
      duracion: 50,
      pasos: [
        {
          clave: 'cp-muestra',
          titulo: 'Muestra nueva',
          minutosSugeridos: 2,
          herramienta: 'grabadora',
          grabadora: true,
          objetivo: 'Guardar la referencia del checkpoint.',
          porQue: 'Seguimiento longitudinal contra la Semana 0 y los checkpoints anteriores.',
          instrucciones: [
            'Dos minutos sin preparar, tema nuevo.',
            'Guardala con la etiqueta del checkpoint.',
          ],
          uso: {
            queHacer:
              'Grabá 2 minutos sobre un tema nuevo y guardala con la etiqueta del checkpoint (W4, W8 o W12).',
          },
          areas: ['habla_espontanea', 'pronunciacion_prosodia'],
        },
        {
          clave: 'cp-voice',
          titulo: 'Checkpoint por Voice',
          minutosSugeridos: 25,
          herramienta: 'chatgpt',
          prompt: 'checkpoint',
          objetivo: 'Evaluación amplia de varias funciones comunicativas.',
          porQue:
            'Incluir varias funciones evita medir solo la que mejor te sale ese día.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Nada de coaching durante la performance.',
            'Completá conversación, historia, explicación, opinión y role-play.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y completá las cinco funciones sin coaching.',
          },
          areas: [
            'habla_espontanea',
            'recuperacion_lexica',
            'naturalidad',
            'interaccion_turnos',
            'gramatica_fluidez',
          ],
        },
        {
          clave: 'cp-listening',
          titulo: 'Muestra auténtica de listening',
          minutosSugeridos: 10,
          herramienta: 'muestraListening',
          objetivo: 'Medir comprensión de habla desconocida a velocidad natural.',
          porQue:
            'Clip nuevo, velocidad natural, sin apoyo visual en el primer intento. Es la condición más parecida a una llamada de trabajo real.',
          instrucciones: [
            'Abrí un clip NUEVO de entre 30 y 90 segundos que no hayas escuchado antes.',
            'Primera escucha SIN texto.',
            'Resumí en inglés lo que entendiste.',
            'Segunda escucha, todavía sin texto.',
            'Comprobá al final con la transcripción y anotá la dificultad percibida.',
          ],
          uso: {
            queHacer:
              'Elegí una fuente sugerida, tomá un fragmento nuevo de 30–90 segundos, escuchalo dos veces sin texto resumiendo entre medio, y comprobá al final.',
            fuentesSugeridas: [
              'npr',
              'thisAmericanLife',
              'pbsNewsHour',
              'youtubeCanalPropio',
              'podcastPropio',
            ],
            fallbackSinFuente: FALLBACK_SIN_FUENTE,
          },
          areas: ['listening'],
        },
        {
          clave: 'cp-lectura-vs-habla',
          titulo: 'Lectura vs improvisación',
          minutosSugeridos: 8,
          herramienta: 'grabadora',
          grabadora: true,
          objetivo: 'Observar tu pronunciación bajo dos cargas distintas.',
          porQue:
            'Ayuda a separar articulación de automaticidad. Si leyendo sale bien e improvisando no, el problema no son los sonidos.',
          instrucciones: [
            'Leé un párrafo corto en voz alta y grabate.',
            'Explicá lo mismo con tus palabras sin mirar, y grabate.',
            'Escuchá las dos y compará con las del baseline.',
          ],
          uso: {
            queHacer:
              'Grabá dos muestras —una leyendo y otra improvisando— y comparalas entre sí y con las del baseline.',
          },
          areas: ['pronunciacion_prosodia', 'habla_espontanea'],
        },
        {
          clave: 'cp-resumen',
          titulo: 'Resumen del checkpoint',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 5 minutos.',
          objetivo: 'Definir el siguiente ajuste del programa.',
          porQue: 'Un checkpoint sirve si cambia algo. Si no, es solo una sesión más.',
          instrucciones: [
            'Guardá las tendencias que notás, no el resultado de un día.',
            'Definí 1 o 2 prioridades para el bloque siguiente.',
            'No cambies el plan entero por un solo mal día.',
          ],
        },
      ],
    },
  },
};
